import { type LeagueDto, type PublicRoomState } from '@spin/shared';
import { useEffect, useState } from 'react';
import { game } from '../api/game';
import { http } from '../api/http';
import {
  BoostersIcon,
  BottleMenuIcon,
  ExitIcon,
  GearIcon,
  HeartIcon,
  MedalIcon,
  MenuIcon,
  RatingsIcon,
  RefreshIcon,
  StylingIcon,
  TrophyIcon,
  WheelIcon,
} from '../components/icons';
import { Emoji } from '../components/Emoji';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';
import { useUi } from '../store/ui';
import { tg } from '../tg/bridge';
import { tableNumber } from './layout';

/** Seconds after sitting down before the table can be changed (the reference counts down on the button). */
const CHANGE_COOLDOWN_S = 5;

/** The league badge under the trophy: "#rank" while the player is in today's league, "!" otherwise. */
function useLeagueBadge(): string {
  const [league, setLeague] = useState<LeagueDto | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () =>
      http
        .get<LeagueDto>('/league')
        .then((l) => alive && setLeague(l))
        .catch(() => undefined);
    void load();
    const id = setInterval(() => void load(), 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  return league?.me ? `#${league.me.rank}` : '!';
}

/** The table's top bar: hearts, wheel, league, menu, settings, exit and the table number with "change table". */
export function TopBar({ room, onExit }: { room: PublicRoomState; onExit: () => void }) {
  const t = useT();
  const balance = useSession((s) => s.me?.balance ?? 0);
  const openDialog = useUi((s) => s.openDialog);
  const openSheet = useUi((s) => s.openSheet);
  const [menu, setMenu] = useState(false);
  const badge = useLeagueBadge();
  const [cooldown, setCooldown] = useState({ room: room.id, left: CHANGE_COOLDOWN_S });
  const left = cooldown.room === room.id ? cooldown.left : CHANGE_COOLDOWN_S;

  useEffect(() => {
    if (left <= 0) return;
    const id = setTimeout(() => setCooldown({ room: room.id, left: left - 1 }), 1000);
    return () => clearTimeout(id);
  }, [left, room.id]);

  const changeTable = async () => {
    if (left > 0) return;
    try {
      await game.change();
      tg.haptic('light');
    } catch (err) {
      reportError(err);
    }
  };

  const pick = (action: () => void) => () => {
    setMenu(false);
    action();
  };

  return (
    <>
      <div className="bar" data-testid="top-bar">
        <button
          className="bar-hearts"
          style={{ left: '2.2cqw' }}
          onClick={() => openDialog('shop')}
          title={t('bar.heart_shop')}
          aria-label={t('bar.heart_shop')}
          data-testid="open-shop"
        >
          <HeartIcon />
          <span className="bar-count" data-testid="balance">
            {balance}
          </span>
        </button>
        <button
          className="bar-button"
          style={{ left: '25%' }}
          onClick={() => openDialog('wheel')}
          title={t('bar.wheel')}
          aria-label={t('bar.wheel')}
          data-testid="open-wheel"
        >
          <WheelIcon />
        </button>
        <button
          className="bar-button"
          style={{ left: '37.6%' }}
          onClick={() => openDialog('league')}
          title={t('bar.league')}
          aria-label={t('bar.league')}
          data-testid="open-league"
        >
          <TrophyIcon />
          <span className="bar-badge" data-testid="league-badge">
            {badge}
          </span>
        </button>
        <button
          className="bar-button"
          style={{ left: '50%' }}
          onClick={() => setMenu((m) => !m)}
          title={t('bar.misc')}
          aria-label={t('bar.misc')}
          aria-expanded={menu}
          data-testid="open-menu"
        >
          <MenuIcon />
        </button>
        <button
          className="bar-button"
          style={{ left: '62.4%' }}
          onClick={() => openDialog('settings')}
          title={t('bar.settings')}
          aria-label={t('bar.settings')}
          data-testid="open-settings"
        >
          <GearIcon />
        </button>
        <button
          className="bar-button"
          style={{ left: '74.5%' }}
          onClick={onExit}
          title={t('bar.exit')}
          aria-label={t('bar.exit')}
          data-testid="exit"
        >
          <ExitIcon />
        </button>
        <span className="bar-table" style={{ left: '86.4%' }} data-testid="table-number">
          {t('bar.table')}
          <br />
          {tableNumber(room.id)}
        </span>
        <button
          className="bar-button"
          style={{ left: '94.2%' }}
          onClick={() => void changeTable()}
          disabled={left > 0}
          title={t('bar.change_table')}
          aria-label={t('bar.change_table')}
          data-testid="change-table"
        >
          {left > 0 ? <span className="bar-countdown">{left}</span> : <RefreshIcon />}
        </button>
      </div>
      {menu && (
        <>
          <div className="menu-catcher" onClick={() => setMenu(false)} />
          <div className="game-menu" role="menu" data-testid="game-menu">
            <button role="menuitem" onClick={pick(() => openDialog('achievements'))}>
              <MedalIcon />
              {t('menu.achievements')}
            </button>
            <button role="menuitem" onClick={pick(() => openDialog('ratings'))}>
              <RatingsIcon />
              {t('menu.ratings')}
            </button>
            <button role="menuitem" onClick={pick(() => openSheet('bottle'))} data-testid="change-bottle">
              <BottleMenuIcon />
              {t('menu.bottle')}
            </button>
            <button role="menuitem" onClick={pick(() => openDialog('styling'))}>
              <StylingIcon />
              {t('menu.styling')}
            </button>
            <button role="menuitem" onClick={pick(() => openDialog('boosters'))}>
              <BoostersIcon />
              {t('menu.boosters')}
            </button>
            <button
              role="menuitem"
              onClick={pick(() => openDialog('horoscope'))}
              data-testid="open-horoscope"
            >
              <Emoji e="🔮" />
              {t('menu.horoscope')}
            </button>
          </div>
        </>
      )}
    </>
  );
}
